import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import Layout from "../../components/Layout";
import { DE } from "../../Languages/DE";
import { EN } from "../../Languages/EN";
import LanguageContext from "../../helper/LanguageContext";
import { callAPI } from "../../helper/apiUtils";
import { Scrollbars } from "react-custom-scrollbars";
import { BASE_URL } from "../../helper/constants";
import { Animated } from "react-animated-css";
import { convertBranchRole, convertPosition } from "../../helper/convertor";
import { connect, useDispatch } from "react-redux";
import { ReactComponent as AddcompanyIconNew } from '../../Icons/Add company-deactivated.svg';
import { openCompanyAdmin, openCompanySettingsFor } from "../Cockpit/cockpitNavigation";
import "./mycompany.css";

// The app's gate for the Admin Center (Kjuup+ company admins): viewerAssignsCompanySeats.
function runsAdminCenter(company) {
  if (!company || !company.isKjuup || company.stripeFeatureEnabled === false) return false;
  if (company.role === "ROLE_COMPANY_SUPER_ADMIN") return true;
  return String(company.position || "").trim().toLowerCase() === "company admin";
}

const renderThumb = ({ style, ...props }) => {
  const thumbStyle = {
    borderRadius: 6,
    backgroundColor: "rgba(var(--kjuup-accent-rgb), 0.8)",
  };
  return (
    <div
      className="thumb-style"
      style={{ ...style, ...thumbStyle }}
      {...props}
    />
  );
};

const CustomScrollbars = (props) => (
  <Scrollbars
    renderThumbHorizontal={renderThumb}
    renderThumbVertical={renderThumb}
    {...props}
  />
);

function MyCompany(props) {
  const { isGerm, strings } = React.useContext(LanguageContext);
  const [followStatus, setFollowStatus] = useState(null);
  const history = useHistory();
  const [companies, setCompanies] = useState([]);
  const companiesRef = useRef([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isContentReady, setIsContentReady] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const location = useLocation();
  const { isDeskTopResolution } = props;
  const getCompanies = async () => {
    const silent = companiesRef.current.length > 0;
    if (!silent) {
      setIsLoading(true);
      setIsContentReady(false);
      setShowSkeleton(true);
    }
    try {
      const { data } = await callAPI(`/company/my`, {}, "GET");
      const next = data.status ? (data.companies || []) : (silent ? companiesRef.current : []);
      companiesRef.current = next;
      setCompanies(next);
    } catch (error) {
      console.error('Error loading companies:', error);
      if (!silent) {
        companiesRef.current = [];
        setCompanies([]);
      }
    } finally {
      setIsLoading(false);
      setShowSkeleton(false);
      setIsContentReady(true);
    }
  };
  useEffect(() => {
    if (location.pathname !== "/my-companies") return;
    getCompanies();
  }, [location.pathname]);

  const getCompanyDetails = (id) => {
    history.push({
      pathname: "/cockpit",
      search: `?tab=company&company=${encodeURIComponent(id)}`,
      state: { id: id },
    });
  };

  // The app's company links (MyCompanies summary): settings for every member,
  // followers, and the employees section of the Admin-Bereich for company admins.
  const dispatch = useDispatch();
  const [settingsBusyId, setSettingsBusyId] = useState(null);
  const openSettingsFor = async (company) => {
    if (settingsBusyId) return;
    setSettingsBusyId(company.id);
    try {
      await openCompanySettingsFor(dispatch, history, company);
    } finally {
      setSettingsBusyId(null);
    }
  };
  const followCompany = async (id) => {
    setFollowStatus(id);
    const { data } = await callAPI(
      `/company/${id}/follow`,
      {
        id: id,
      },
      "GET"
    );
    console.log(data);
    setFollowStatus(null);
    getCompanies();
  };

  return (
    <React.Fragment>
      {isLoading || showSkeleton ? (
        <div className={`company_section company_list loading my-company my-company--skeleton ${!showSkeleton ? 'skeleton-fade-out my-company--skeleton-out' : ''}`}>
          {/* Skeleton Company List Items */}
          <div className="company_list_ver kj-flex-1">
            <div className="my-scroll min_height m-0 h-100">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="bg-purple my-company__skeleton-item">
                  <div className="my-company__skeleton-row">
                    <div className="my-company__skeleton-lead">
                      <div className="skeleton skeleton-image my-company__sk-logo"></div>
                      <div className="skeleton skeleton-icon my-company__sk-icon"></div>
                    </div>
                    <div className="my-company__skeleton-stats">
                      <div className="text-center">
                        <div className="skeleton skeleton-stat my-company__sk-stat"></div>
                        <div className="skeleton skeleton-text short my-company__sk-stat-label"></div>
                      </div>
                      <div className="text-center">
                        <div className="skeleton skeleton-stat my-company__sk-stat"></div>
                        <div className="skeleton skeleton-text short my-company__sk-stat-label"></div>
                      </div>
                    </div>
                  </div>
                  <div className="profile-flied my-company__skeleton-body">
                    <div className="skeleton skeleton-title my-company__sk-title"></div>
                    <div className="skeleton skeleton-text my-company__sk-line my-company__sk-line--80"></div>
                    <div className="skeleton skeleton-text my-company__sk-line my-company__sk-line--50"></div>
                    <div className="skeleton skeleton-text my-company__sk-line--40"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className={`company_section company_list loaded my-company my-company--loaded${showSkeleton ? ' my-company--hidden' : ''} ${isContentReady ? 'company-content-fade-in' : ''}`}>
          <div className="page-header my-company__header">
            <h2 className="page-header-title">{strings.my_companies}</h2>
            <div className="page-header-actions">
              <button
                onClick={() => {
                  history.push("/add-company")
                }}
                className="profile-add-company-btn kj-btn kj-btn--outline my-company__add-btn"
              >
                {strings.add_or_join_a_company}
                <AddcompanyIconNew height={'20px'} width={'20px'} />
              </button>
            </div>
          </div>
          <div className="company_list_ver kj-flex-1">
            <div className="my-scroll min_height m-0 h-100">
              <CustomScrollbars
                autoHide
                autoHideTimeout={500}
                autoHideDuration={200}
                className="bottom-bar"
              >
                {companies && companies.length > 0 ? (
                  companies.map((company, index) => {
                    let add = `${company.houseNo ? company.houseNo + "," : ""} ${company.street}, ${company.city}, ${company.postCode ? company.postCode + "," : ""} ${company.country ? company.country : ''}`
                    return (
                      <div 
                        className="bg-purple my-company__item"
                        key={company.id}
                        style={{ "--item-delay": `${index * 0.05}s` }}
                        onClick={(e) => {
                          e.preventDefault();
                          getCompanyDetails(company.id);
                        }}
                      >
                        <div className="my-company__item-row">
                          <div className="user_img b-4 my-company__logo-wrap">
                            {company.logo ? (
                              <img
                                width={40}
                                height={40}
                                src={BASE_URL + company.logo}
                                alt="Generic placeholder"
                                className="my-company__logo"
                                style={{ opacity: 0 }}
                                onLoad={(e) => {
                                  if (e.target) {
                                    e.target.style.opacity = '1';
                                    e.target.classList.add('loaded');
                                  }
                                }}
                                onError={(e) => {
                                  if (e.target) {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              />
                            ) : (
                              <div className="company_img_placeholder" aria-hidden="true">
                                <span className="material-symbols-outlined">image</span>
                              </div>
                            )}
                          </div>
                          <div className="profile-flied my-company__info">
                            <div className="profile_name my-company__name">
                              <h5 className="pointer">
                                {company.name}
                              </h5>
                              <p>
                                {company.full_address ? company.full_address : add}
                              </p>
                              <p>{convertBranchRole(company.branch, isGerm)}</p>
                              <p>
                                {strings.my_position}{" "}
                                {convertPosition(isGerm, company.position)}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- only keeps clicks on the buttons from opening the company */}
                        <div className="my-company__actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`kj-btn kj-btn--secondary kj-btn--sm my-company__action${settingsBusyId === company.id ? " kj-btn--loading" : ""}`}
                            onClick={() => openSettingsFor(company)}
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">settings</span>
                            {strings.cockpit_company_settings || (isGerm ? "Firmeneinstellungen" : "Company settings")}
                          </button>
                          {runsAdminCenter(company) ? (
                            <button
                              type="button"
                              className="kj-btn kj-btn--secondary kj-btn--sm my-company__action"
                              onClick={() => openCompanyAdmin(dispatch, history, company.id, "dept-employees")}
                            >
                              <span className="material-symbols-outlined" aria-hidden="true">groups</span>
                              {strings.employees || (isGerm ? "Mitarbeitende" : "Employees")}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="kj-btn kj-btn--secondary kj-btn--sm my-company__action"
                            onClick={() => history.push(`/company-followers/${company.id}`)}
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">rss_feed</span>
                            {strings.followers || (isGerm ? "Abonnenten" : "Followers")}
                            {Number.isFinite(Number(company.followerCount)) ? ` · ${Number(company.followerCount)}` : ""}
                          </button>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="bg-purple">
                    <p className="text-center my-company__empty">
                      {isGerm ? 'Keine Firmen gefunden' : 'No companies found'}
                    </p>
                  </div>
                )}
              </CustomScrollbars>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({
  isDeskTopResolution: state.isDeskTopResolution
});

export default connect(mapStateToProps)(MyCompany);
